<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div style="width: 100%; max-width: 1200px;">

      <!-- Week pickers header -->
      <div class="table-header-filters pa-4 mb-6">
        <v-row dense align="center">

          <!-- Week pickers as pills -->
          <v-col cols="12" sm="auto">
            <div class="period-pills">
              <v-menu v-model="olderMenu" offset-y :close-on-content-click="false" min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <button class="period-pill date-pill older-week-pill" v-bind="attrs" v-on="on">
                    <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                    {{ weekLabel(olderWeekStart) }}
                  </button>
                </template>
                <v-date-picker no-title first-day-of-week="1" :value="datePickerValue(olderWeekStart)" @input="onOlderDatePicked" />
              </v-menu>
              <span class="date-pill-sep">vs</span>
              <v-menu v-model="currentMenu" offset-y :close-on-content-click="false" min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <button class="period-pill date-pill current-week-pill" v-bind="attrs" v-on="on">
                    <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                    {{ weekLabel(currentWeekStart) }}
                  </button>
                </template>
                <v-date-picker no-title first-day-of-week="1" :value="datePickerValue(currentWeekStart)" @input="onCurrentDatePicked" />
              </v-menu>
            </div>
          </v-col>

          <v-col cols="auto" class="d-flex align-center ml-auto">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon :to="{ name: `${targetModifier}.all-reports.reportMenu` }" class="close-btn" v-bind="attrs" v-on="on">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </template>
              <span>Zatvori izvještaj</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="#9C27B0" size="48" />
        <div class="mt-4 grey--text">Učitavanje podataka…</div>
      </div>

      <template v-else>

        <!-- Section A: Grouped bar chart -->
        <v-card outlined rounded="lg" class="pa-6 mb-6">
          <div class="section-title mb-4">Usporedba dan po dan</div>
          <apexchart
            type="bar"
            height="300"
            :options="chartOptions"
            :series="chartSeries"
          />
        </v-card>

        <!-- Section B: Daily table -->
        <v-card outlined rounded="lg" class="pa-6 mb-6">
          <div class="section-title mb-4">Detalji po danima</div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Dan</th>
                <th class="text-right" style="color: #9C27B0;">{{ weekLabel(olderWeekStart) }}</th>
                <th class="text-right" style="color: #00838F;">{{ weekLabel(currentWeekStart) }}</th>
                <th class="text-right">Δ%</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in tableRows" :key="i">
                <td>{{ row.dayName }}</td>
                <td class="text-right">{{ row.olderFormatted }}</td>
                <td class="text-right">{{ row.currentFormatted }}</td>
                <td class="text-right">
                  <span
                    v-if="row.delta !== null"
                    :style="{ color: row.delta >= 0 ? '#4CAF50' : '#F44336', fontWeight: '600' }"
                  >
                    <v-icon x-small :color="row.delta >= 0 ? '#4CAF50' : '#F44336'">
                      {{ row.delta >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                    </v-icon>
                    {{ Math.abs(row.delta).toFixed(1) }}%
                  </span>
                  <span v-else style="color: #9CA3AF;">—</span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr style="background: #F8FAFC; border-top: 2px solid #E0E6ED;">
                <td><strong>Ukupno</strong></td>
                <td class="text-right"><strong>{{ formatEur(olderTotal) }}</strong></td>
                <td class="text-right"><strong>{{ formatEur(currentTotal) }}</strong></td>
                <td class="text-right">
                  <span
                    v-if="olderTotal > 0 && currentTotal > 0"
                    :style="{ color: totalDelta >= 0 ? '#4CAF50' : '#F44336', fontWeight: '600' }"
                  >
                    <v-icon x-small :color="totalDelta >= 0 ? '#4CAF50' : '#F44336'">
                      {{ totalDelta >= 0 ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
                    </v-icon>
                    {{ Math.abs(totalDelta).toFixed(1) }}%
                  </span>
                  <span v-else style="color: #9CA3AF;">—</span>
                </td>
              </tr>
            </tfoot>
          </v-simple-table>
        </v-card>

      </template>
    </div>
  </v-container>
</template>

<script>
import { getFirestore, collection, query, where, getAggregateFromServer, sum, count } from 'firebase/firestore'
import state from '@/state'
import EventBus from '@/plugins/event-bus'
import VueApexCharts from 'vue-apexcharts'
import Vue from 'vue'

Vue.use(VueApexCharts)
Vue.component('apexchart', VueApexCharts)

const DAY_NAMES = ['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned']
const COLOR_OLDER = '#9C27B0'
const COLOR_CURRENT = '#00BCD4'

function mondayOf (d) {
  const date = new Date(d)
  const day = date.getDay() || 7
  date.setDate(date.getDate() - day + 1)
  date.setHours(0, 0, 0, 0)
  return date
}

export default {
  name: 'UsporedbaPoTjednima',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],

  data () {
    const today = new Date()
    const thisMonday = mondayOf(today)
    const lastMonday = new Date(thisMonday)
    lastMonday.setDate(lastMonday.getDate() - 7)
    return {
      margin: '0px',
      targetModifier: '',
      loading: false,
      olderMenu: false,
      currentMenu: false,
      olderWeekStart: lastMonday,
      currentWeekStart: thisMonday,
      weekDataOlder: Array(7).fill(null),
      weekDataCurrent: Array(7).fill(null)
    }
  },

  computed: {
    olderTotal () {
      return this.weekDataOlder.reduce((acc, v) => acc + (v || 0), 0) / 100
    },
    currentTotal () {
      return this.weekDataCurrent.reduce((acc, v) => acc + (v || 0), 0) / 100
    },
    totalDelta () {
      if (!this.olderTotal) return 0
      return (this.currentTotal - this.olderTotal) / this.olderTotal * 100
    },

    tableRows () {
      return DAY_NAMES.map((name, i) => {
        const older = this.weekDataOlder[i]
        const current = this.weekDataCurrent[i]
        const delta = (older !== null && older > 0 && current !== null)
          ? (current - older) / older * 100
          : null
        return {
          dayName: name,
          olderFormatted: older !== null ? this.formatEur(older / 100) : '—',
          currentFormatted: current !== null ? this.formatEur(current / 100) : '—',
          delta
        }
      })
    },

    chartOptions () {
      return {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Raleway, sans-serif' },
        plotOptions: { bar: { horizontal: false, columnWidth: '70%' } },
        colors: [COLOR_OLDER, COLOR_CURRENT],
        xaxis: { categories: DAY_NAMES },
        yaxis: { labels: { formatter: v => this.formatEur(v) } },
        dataLabels: { enabled: false },
        legend: { show: true },
        tooltip: { y: { formatter: v => this.formatEur(v) } }
      }
    },

    chartSeries () {
      return [
        {
          name: this.weekLabel(this.olderWeekStart),
          data: this.weekDataOlder.map(v => parseFloat(((v || 0) / 100).toFixed(2)))
        },
        {
          name: this.weekLabel(this.currentWeekStart),
          data: this.weekDataCurrent.map(v => parseFloat(((v || 0) / 100).toFixed(2)))
        }
      ]
    }
  },

  async created () {
    this.margin = state.getMargin()
    const sideNav = this.$route.meta?.sideNav
    if (sideNav === 'Company') this.targetModifier = 'company'
    else if (sideNav === 'PointOfSale') this.targetModifier = 'pointOfSale'
    else if (sideNav === 'CashRegister') this.targetModifier = 'cashRegister'
    EventBus.$on('set-mini', () => { this.margin = state.getMargin() })
    await this.fetchBothWeeks()
  },

  beforeDestroy () {
    EventBus.$off('set-mini')
  },

  methods: {
    mondayOfDate (d) {
      return mondayOf(d)
    },

    datePickerValue (weekStart) {
      const d = new Date(weekStart)
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    },

    weekLabel (weekStart) {
      const start = new Date(weekStart)
      const end = new Date(weekStart)
      end.setDate(end.getDate() + 6)
      const fmt = d => `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`
      return `${fmt(start)} – ${fmt(end)}.${end.getFullYear()}`
    },

    formatEur (valueEur) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(valueEur)
    },

    onOlderDatePicked (dateStr) {
      this.olderWeekStart = this.mondayOfDate(new Date(dateStr))
      this.olderMenu = false
      this.fetchBothWeeks()
    },

    onCurrentDatePicked (dateStr) {
      this.currentWeekStart = this.mondayOfDate(new Date(dateStr))
      this.currentMenu = false
      this.fetchBothWeeks()
    },

    async fetchWeekData (weekStart) {
      const companyId = state.getCurrentCompany().id
      const db = getFirestore()
      const col = collection(db, `receipts/${companyId}/receipts`)

      const results = await Promise.all(
        Array.from({ length: 7 }, (_, i) => {
          const dayStart = new Date(weekStart)
          dayStart.setDate(dayStart.getDate() + i)
          dayStart.setHours(0, 0, 0, 0)
          const dayEnd = new Date(dayStart)
          dayEnd.setDate(dayEnd.getDate() + 1)
          const tsStart = Math.floor(dayStart.getTime() / 1000)
          const tsEnd = Math.floor(dayEnd.getTime() / 1000)
          const q = query(
            col,
            where('issue_date_time', '>=', tsStart),
            where('issue_date_time', '<', tsEnd)
          )
          return getAggregateFromServer(q, { total: sum('total'), cnt: count() })
        })
      )

      return results.map(snap => {
        const val = snap.data().total
        return (val !== null && val > 0) ? val : null
      })
    },

    async fetchBothWeeks () {
      this.loading = true
      try {
        const [older, current] = await Promise.all([
          this.fetchWeekData(this.olderWeekStart),
          this.fetchWeekData(this.currentWeekStart)
        ])
        this.weekDataOlder = older
        this.weekDataCurrent = current
      } catch (err) {
        console.error('UsporedbaPoTjednima:', err)
        this.showMsgBox({ text: 'Greška pri učitavanju podataka.', actions: ['cancel'] })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}

.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}

.table-header-filters {
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #2d3748;
}

.period-pills {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.period-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;
  cursor: pointer;
  border: 1.5px solid #E2E8F0;
  background: #fff;
  transition: all 0.15s;
  white-space: nowrap;
  outline: none;
}
.date-pill {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.3px;
}
.date-pill-sep {
  font-size: 12px;
  color: #9CA3AF;
  font-weight: 600;
}
.older-week-pill {
  border-color: rgba(156, 39, 176, 0.4) !important;
  color: #9C27B0 !important;
  background: rgba(156, 39, 176, 0.05) !important;
}
.older-week-pill:hover {
  background: rgba(156, 39, 176, 0.1) !important;
}
.current-week-pill {
  border-color: rgba(0, 188, 212, 0.5) !important;
  color: #00838F !important;
  background: rgba(0, 188, 212, 0.05) !important;
}
.current-week-pill:hover {
  background: rgba(0, 188, 212, 0.1) !important;
}
</style>
